import { useCallback } from "react";

import { useJitsu } from "@jitsu/react";
import { useParams } from "react-router-dom";

import { JitsuEvent } from "@/data/events/JitsuEvent";
import { useAppSelector } from "@/data/hooks";
import { getAssessmentId, getTaskId } from "@/data/task";
import {
  IPastAssessment,
  PreviewPastAssessmentCallback,
} from "@/data/templates";
import { TaskFormat } from "@/generated/graphql";
import { useAssessmentSettingsFragment } from "@/graphql/assessment-selectors";
import { useFeatureFlagsFragment } from "@/graphql/institution-selectors";
import { useTaskFormatFragment } from "@/graphql/task-selectors";
import { usePastAssessments } from "@/ui/templates/templates-hooks";

import { useShowPastAssessmentPreviewModal } from "./modals";
import { PastAssessmentsRoot } from "./my-assessments/past-assessments-root";
import { useOnDuplicatePastAssessment } from "./preview/use-on-duplicate-past-assessment";

export const ConnectedMyAssessments = () => {
  const currentAssessmentId = useAppSelector(getAssessmentId);
  const onPreview = useOnPreview();

  const onDuplicatePastAssessment = useOnDuplicatePastAssessment();

  const features = useFeatureFlagsFragment();
  const examsFeatureEnabled = features?.exams === true;

  const currentTaskId = useAppSelector(getTaskId);
  const { assessmentId: routeAssessmentId = "", taskId: routeTaskId = "" } =
    useParams();
  // Single effective id so the past-assessments query (server-side
  // compatibility filtering) and `isGroupAssessment` (client-side exclusion)
  // stay consistent — `currentAssessmentId` can be null on first render.
  const effectiveAssessmentId = currentAssessmentId ?? routeAssessmentId;
  const effectiveTaskId = currentTaskId ?? routeTaskId;
  const settings = useAssessmentSettingsFragment(effectiveAssessmentId);
  const isGroupAssessment = settings?.groupAssessmentEnabled === true;

  // When the destination task is multi-format, templates are constrained to
  // multi-format sources — default the filter and hide the Format dropdown.
  const taskFormat = useTaskFormatFragment(effectiveTaskId);
  const isMultiformat = taskFormat?.format === TaskFormat.Multiformat;

  const { loading, pastAssessments } = usePastAssessments(
    effectiveAssessmentId || null
  );

  return (
    <PastAssessmentsRoot
      areExamsEnabled={examsFeatureEnabled}
      isGroupAssessment={isGroupAssessment}
      isMultiformat={isMultiformat}
      pastAssessments={pastAssessments ?? []}
      loading={loading}
      onDuplicate={onDuplicatePastAssessment}
      onPreview={onPreview}
    />
  );
};
const useOnPreview = (): PreviewPastAssessmentCallback => {
  const showPreviewModal = useShowPastAssessmentPreviewModal();
  const { track } = useJitsu();
  return useCallback(
    (pastAssessment: IPastAssessment, instructionSheetVersionId: string) => {
      showPreviewModal({
        pastAssessment: {
          ...pastAssessment,
          id: pastAssessment.assessmentId,
        },
        instructionSheetVersionId,
      });
      track(JitsuEvent.TEMPLATE_SHEET_PREVIEWED, {
        assessment_id: pastAssessment.assessmentId,
        assessment_name: pastAssessment.name,
        sheet_id: instructionSheetVersionId,
      });
    },
    [showPreviewModal, track]
  );
};
